import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatusBadge } from "../components/StatusBadge";
import { Upload, Music2, Clock, CheckCircle2, XCircle, Radio, ListMusic, DollarSign, Search, TrendingUp, TrendingDown, ArrowRight, Youtube, PlayCircle } from "lucide-react";
import { PLATFORMS } from "./UserDsp";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const STATUS_COLORS = {
  "Under Review": "#a78bfa",
  "Approved":     "#22d3ee",
  "Released":     "#10b981",
  "Rejected":     "#f43f5e",
  "Submitted":    "#71717a",
};

function fmtINR(v) {
  const n = Number(v || 0);
  return "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}
function pctBadge(v) {
  if (v === null || v === undefined) return null;
  const up = v >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-emerald-400" : "text-rose-400"}`}>
      <Icon className="w-3 h-3" /> {up ? "+" : ""}{v}%
    </span>
  );
}
function pickWindow(series, key, w) {
  if (w === "all") return series;
  const n = w === "3m" ? 3 : w === "6m" ? 6 : 12;
  return series.slice(-n);
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [revWin, setRevWin] = useState("12m");
  const [ytWin, setYtWin] = useState("12m");
  const [relWin, setRelWin] = useState("12m");

  useEffect(() => { api.get("/dashboard/analytics").then((r) => setData(r.data)); }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      if (q.trim()) api.get("/submissions", { params: { q: q.trim() } }).then((r) => setResults(r.data));
      else setResults([]);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const s = data?.summary;
  const yt = data?.youtube;
  const dsp = data?.dsp;

  const monthly = data?.monthly_revenue || [];
  const monthlyView = useMemo(() => pickWindow(monthly, "month", revWin), [monthly, revWin]);
  const ytMonthlyView = useMemo(() => pickWindow(yt?.monthly || [], "month", ytWin), [yt?.monthly, ytWin]);
  const relTrendView = useMemo(() => pickWindow(data?.release_trend || [], "month", relWin), [data?.release_trend, relWin]);

  const cards = [
    { label: "Total Songs", value: s?.total_songs ?? 0, icon: ListMusic, tone: "cyan", testid: "stat-total-songs" },
    { label: "Digital Revenue", value: fmtINR(s?.digital_revenue), icon: DollarSign, tone: "emerald", testid: "stat-digital-rev" },
    { label: "YouTube Revenue", value: fmtINR(s?.youtube_revenue), icon: Youtube, tone: "rose", testid: "stat-yt-rev" },
    { label: "Rejected", value: s?.by_status?.["Rejected"] ?? 0, icon: XCircle, tone: "rose", testid: "stat-rejected" },
    { label: "Under Review", value: s?.by_status?.["Under Review"] ?? 0, icon: Clock, tone: "violet", testid: "stat-under-review" },
    { label: "Released", value: s?.by_status?.["Released"] ?? 0, icon: Radio, tone: "sky", testid: "stat-released" },
  ];

  const statusPie = Object.entries(s?.by_status || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: k, value: v, fill: STATUS_COLORS[k] || "#71717a" }));

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400 flex items-center gap-2">
            <img src="/logo.png" alt="Sonotek Digital" className="h-5 w-auto rounded bg-white/95 p-px border border-zinc-800" data-testid="dashboard-logo" />
            Sonotek<span className="gradient-text">Digi</span> · Artist Console
          </div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight">
            Hello, <span className="gradient-text">{user?.full_name?.split(" ")[0] || "Artist"}</span>
          </h1>
          <p className="mt-2 text-zinc-500 text-sm">{user?.phone} · {user?.email}</p>
        </div>
        <Link to="/upload" data-testid="dashboard-upload-btn">
          <Button size="lg" className="rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold hover:brightness-110">
            <Upload className="w-4 h-4 mr-2" /> Upload New Song
          </Button>
        </Link>
      </div>

      {/* Search */}
      <div className="mb-8 relative max-w-2xl">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by Song Name, ISRC or UPC"
          data-testid="dashboard-search" className="pl-9 bg-zinc-900 border-zinc-800 h-11" />
        {results.length > 0 && (
          <div className="absolute top-12 left-0 right-0 rounded-xl border border-zinc-800 bg-zinc-950/95 backdrop-blur z-30 max-h-96 overflow-y-auto shadow-xl" data-testid="search-results">
            {results.slice(0, 8).map((s) => (
              <Link key={s.id} to="/my-songs" state={{ open: s.id }}
                className="flex items-center gap-3 p-3 hover:bg-zinc-900 border-b border-zinc-900 last:border-0" data-testid={`search-result-${s.id}`}>
                <img src={fileUrl(s.artwork_url)} alt="" className="w-10 h-10 rounded-md object-cover border border-zinc-800" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{s.song_title}</div>
                  <div className="text-xs text-zinc-500 truncate">{s.primary_artist_name || s.artist_name} · ISRC {s.isrc || "—"} · UPC {s.upc || "—"}</div>
                </div>
                <StatusBadge status={s.status} />
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Row 1 — Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {cards.map((c) => <StatCard key={c.label} {...c} />)}
      </div>

      {/* Row 2 — Revenue Overview + Song Status */}
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <Card className="lg:col-span-2" title="Revenue Overview" subtitle="Digital + YouTube · same currency (INR)"
          action={<WindowSelect value={revWin} onChange={setRevWin} testid="rev-overview-window" />}
        >
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={monthlyView}>
              <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fill: "#71717a", fontSize: 11 }} />
              <YAxis tick={{ fill: "#71717a", fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${Math.round(v/1000)}k` : v} />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} formatter={(v) => fmtINR(v)} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }} />
              <Line type="monotone" dataKey="digital"  stroke="#22d3ee" strokeWidth={2} dot={false} name="Digital" />
              <Line type="monotone" dataKey="youtube"  stroke="#f43f5e" strokeWidth={2} dot={false} name="YouTube" />
              <Line type="monotone" dataKey="total"    stroke="#a78bfa" strokeWidth={2.5} dot={false} name="Total" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Song Status">
          {statusPie.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={statusPie} innerRadius={55} outerRadius={85} dataKey="value" label={{ fill: "#a1a1aa", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty>No songs yet</Empty>}
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            {statusPie.map((r) => (
              <div key={r.name} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: r.fill }} />
                <span className="text-zinc-400">{r.name}</span>
                <span className="ml-auto font-mono text-zinc-100">{r.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Row 3 — YouTube Monthly + Channels */}
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <Card
          className="lg:col-span-2"
          title="YouTube Monthly Revenue"
          subtitle={yt?.latest_month ? `Latest ${yt.latest_month}: ${fmtINR(yt.latest_month_revenue)}` : "No YouTube data yet"}
          action={<WindowSelect value={ytWin} onChange={setYtWin} testid="yt-window" />}
        >
          <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
            <MiniStat k="Total YT (INR)" v={fmtINR(yt?.total)} testid="yt-total" />
            <MiniStat k={yt?.latest_month ? `Latest (${yt.latest_month})` : "Latest"} v={fmtINR(yt?.latest_month_revenue)} testid="yt-latest" />
            <MiniStat
              k={yt?.previous_month ? `Prev (${yt.previous_month})` : "Previous"}
              v={<span className="flex items-center gap-2">{fmtINR(yt?.previous_month_revenue)} {pctBadge(yt?.change_percent)}</span>}
              testid="yt-prev"
            />
          </div>
          {(yt?.monthly || []).length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={ytMonthlyView}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fill: "#71717a", fontSize: 11 }} />
                <YAxis tick={{ fill: "#71717a", fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${Math.round(v/1000)}k` : v} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} formatter={(v) => fmtINR(v)} />
                <Bar dataKey="amount" fill="#f43f5e" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty>Admin hasn't imported YouTube revenue for you yet.</Empty>}
          <div className="mt-3">
            <Link to="/revenue?tab=yt" className="text-xs text-cyan-400 hover:underline inline-flex items-center gap-1" data-testid="yt-details-link">
              View YouTube Revenue Details <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </Card>
        <Card title="Revenue by Channel" subtitle={yt?.latest_month ? `Month ${yt.latest_month}` : "Latest month"}>
          {(yt?.channels_latest_month || []).length ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart layout="vertical" data={yt.channels_latest_month}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fill: "#71717a", fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${Math.round(v/1000)}k` : v} />
                <YAxis type="category" dataKey="channel" tick={{ fill: "#71717a", fontSize: 10 }} width={110} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} formatter={(v) => fmtINR(v)} />
                <Bar dataKey="amount" fill="#22d3ee" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty>No channel data yet</Empty>}
        </Card>
      </div>

      {/* Row 4 — Platform Coverage + DSP Coverage */}
      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <Card className="lg:col-span-2" title="Platform Coverage" subtitle="Songs live on each DSP">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={Object.entries(PLATFORMS).map(([k, m]) => ({
              platform: m.label, songs: dsp?.per_platform?.[k] || 0, color: m.color,
            }))}>
              <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
              <XAxis dataKey="platform" tick={{ fill: "#71717a", fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
              <YAxis allowDecimals={false} tick={{ fill: "#71717a", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} />
              <Bar dataKey="songs" radius={[6, 6, 0, 0]}>
                {Object.entries(PLATFORMS).map(([k, m]) => <Cell key={k} fill={m.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="DSP Coverage" subtitle={`${dsp?.coverage_percent ?? 0}% of your songs are live on at least one DSP`}>
          {(dsp?.total_records || 0) > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={[
                  { name: "With links", value: dsp.songs_with_links, fill: "#22d3ee" },
                  { name: "No links",   value: dsp.songs_without_links, fill: "#3f3f46" },
                ]} innerRadius={55} outerRadius={85} dataKey="value">
                  <Cell fill="#22d3ee" /><Cell fill="#3f3f46" />
                </Pie>
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty>No DSP records yet</Empty>}
          <div className="mt-3">
            <Link to="/dsp-links" className="text-xs text-cyan-400 hover:underline inline-flex items-center gap-1" data-testid="view-dsp-link">
              View All Platform Links <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </Card>
      </div>

      {/* Row 5 — Your Music Platforms cards */}
      <div className="mb-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Your Music Platforms</h2>
          <Link to="/dsp-links" className="text-sm text-cyan-400 hover:underline inline-flex items-center gap-1">View all <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="platform-cards">
          {Object.entries(PLATFORMS).map(([k, m]) => (
            <div key={k} data-testid={`platform-card-${k}`}
              className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 hover:border-zinc-700 transition-colors">
              <div className="text-[10px] uppercase tracking-wider" style={{ color: m.color }}>{m.label}</div>
              <div className="mt-1 text-xl font-semibold">{dsp?.per_platform?.[k] || 0}</div>
              <div className="text-[10px] text-zinc-500">songs live</div>
            </div>
          ))}
        </div>
      </div>

      {/* Row 6 — Language + Release Trend */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <Card title="Songs by Language">
          {(data?.languages || []).length ? (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={data.languages.slice(0, 10)}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                <XAxis dataKey="language" tick={{ fill: "#71717a", fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fill: "#71717a", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} />
                <Bar dataKey="count" fill="#a78bfa" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty>No language data yet</Empty>}
        </Card>
        <Card title="Release Trend" subtitle="Songs released per month"
          action={<WindowSelect value={relWin} onChange={setRelWin} testid="release-window" hide3m />}
        >
          {(data?.release_trend || []).length ? (
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={relTrendView}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fill: "#71717a", fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fill: "#71717a", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a" }} />
                <Line type="monotone" dataKey="count" stroke="#22d3ee" strokeWidth={2} dot={{ fill: "#22d3ee" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty>No releases yet</Empty>}
        </Card>
      </div>

      {/* Row 7 — Recent Submissions */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-hidden">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <Music2 className="w-5 h-5 text-cyan-400" /> Recent Submissions
          </h2>
          <Link to="/my-songs" className="text-sm text-cyan-400 hover:underline" data-testid="view-all-songs">View all →</Link>
        </div>
        {data?.recent?.length ? (
          <div className="divide-y divide-zinc-800" data-testid="recent-submissions">
            {data.recent.map((s) => (
              <Link key={s.id} to="/my-songs" state={{ open: s.id }}
                data-testid={`recent-row-${s.id}`}
                className="p-4 flex items-center gap-4 hover:bg-zinc-900/40 transition-colors">
                <img src={fileUrl(s.artwork_url)} alt={s.song_title} className="w-14 h-14 rounded-lg object-cover border border-zinc-800" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{s.song_title}</div>
                  <div className="text-sm text-zinc-500 truncate">{s.singer_name || s.primary_artist_name || s.artist_name} · {s.audio_format || "—"}</div>
                </div>
                <div className="text-xs text-zinc-500 hidden md:block">
                  {s.release_date || new Date(s.created_at).toLocaleDateString()}
                </div>
                <StatusBadge status={s.status} />
                <PlayCircle className="w-4 h-4 text-zinc-600 hidden md:block" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Music2 className="w-6 h-6 mx-auto text-zinc-600 mb-3" />
            <p className="text-zinc-400">No submissions yet. Start with your first track.</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Building blocks --- */
function StatCard({ label, value, icon: Icon, tone, testid }) {
  const map = {
    cyan: "border-cyan-500/30 from-cyan-500/10 text-cyan-400",
    emerald: "border-emerald-500/30 from-emerald-500/10 text-emerald-400",
    rose: "border-rose-500/30 from-rose-500/10 text-rose-400",
    violet: "border-violet-500/30 from-violet-500/10 text-violet-400",
    sky: "border-sky-500/30 from-sky-500/10 text-sky-400",
  };
  const [borderCls, gradCls, iconCls] = (map[tone] || map.cyan).split(" ");
  return (
    <div data-testid={testid}
      className={`relative overflow-hidden rounded-2xl border ${borderCls} bg-gradient-to-br ${gradCls} to-transparent p-5`}>
      <Icon className={`w-5 h-5 ${iconCls} mb-6`} />
      <div className="text-2xl font-bold tracking-tight break-words">{value}</div>
      <div className="mt-1 text-xs text-zinc-400 uppercase tracking-wider">{label}</div>
    </div>
  );
}
function Card({ title, subtitle, action, children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-zinc-800 bg-zinc-950/60 p-5 ${className}`}>
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="font-semibold">{title}</div>
          {subtitle && <div className="text-xs text-zinc-500 mt-0.5">{subtitle}</div>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}
function MiniStat({ k, v, testid }) {
  return (
    <div className="p-2 rounded-lg bg-zinc-900/60 border border-zinc-800">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
      <div className="mt-0.5 text-sm font-semibold" data-testid={testid}>{v}</div>
    </div>
  );
}
function WindowSelect({ value, onChange, testid, hide3m }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-zinc-900 border-zinc-800 h-8 text-xs w-36"><SelectValue /></SelectTrigger>
      <SelectContent>
        {!hide3m && <SelectItem value="3m">Last 3 months</SelectItem>}
        <SelectItem value="6m">Last 6 months</SelectItem>
        <SelectItem value="12m">Last 12 months</SelectItem>
        <SelectItem value="all">All time</SelectItem>
      </SelectContent>
    </Select>
  );
}
function Empty({ children }) {
  return <div className="h-40 flex items-center justify-center text-xs text-zinc-500">{children}</div>;
}
